import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { FadeIn, FadeInStagger, FadeInStaggerItem } from '../components/ui/AnimatedSection';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Check, ShieldAlert, Sparkles, Wind, Droplet, Eye, Camera, Shirt, X, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function Methode() {
  const [activeRule, setActiveRule] = useState<number | null>(null);

  // Bloquer le scroll quand le modal est ouvert
  useEffect(() => {
    if (activeRule !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [activeRule]);

  const { t } = useTranslation();
  
  // Base rule data with icons/images, merged with translated text
  const baseRules = [
    { icon: <ShieldAlert strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule1.webp" },
    { icon: <Droplet strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule2.webp" },
    { icon: <Shirt strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule3.webp" },
    { icon: <Wind strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule4.webp" },
    { icon: <Sparkles strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule5.webp" },
    { icon: <Eye strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule6.webp" },
    { icon: <Check strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule7.webp" },
    { icon: <Camera strokeWidth={1} className="w-12 h-12 mb-6" />, image: "/assets/rules/rule8.webp" }
  ];

  const translatedRules = t('methode.rules', { returnObjects: true }) as Array<{ title: string; subtitle: string; detail: string }>;
  const rules = baseRules.map((rule, idx) => ({ ...rule, ...translatedRules[idx] }));

  return (
    <div className="min-h-screen flex flex-col font-sans text-black-deep bg-black-deep">
      <Navbar />
      
      <main className="flex-grow">
        
        {/* HERO SECTION METHODE */}
        <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-transform duration-[20s] ease-out hover:scale-110"
            style={{ backgroundImage: 'url("/assets/luxury_gloves.webp")' }}
          >
            <div className="absolute inset-0 bg-black-deep/80"></div>
          </div>
          <FadeIn className="relative z-10 text-center px-4 mt-20">
            <span className="text-gold-400 font-serif italic text-xl md:text-2xl mb-4 block tracking-wide">
              {t('methode.heroTag')}
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif text-white leading-tight drop-shadow-lg">
              {t('methode.heroTitle1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 italic">{t('methode.heroTitle2')}</span>
            </h1>
          </FadeIn>
        </div>

        {/* SECTION 1: LE MANIFESTE */}
        <section className="py-24 md:py-40 bg-black-deep text-white relative border-t border-white/5">
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gold-900/10 rounded-full blur-[150px] -translate-x-1/2 -translate-y-1/2"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
              
              <div className="w-full lg:w-5/12">
                <div className="lg:sticky lg:top-40">
                  <FadeIn>
                    <span className="text-gold-400 tracking-[0.4em] text-xs font-light uppercase mb-8 block flex items-center">
                      <span className="w-8 h-px bg-gold-500 mr-4"></span>
                      {t('methode.manifestoTag')}
                    </span>
                    <h2 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white leading-tight font-light mb-8">
                      {t('methode.manifestoTitle1')} <br/>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 font-serif italic">{t('methode.manifestoTitle2')}</span>
                    </h2>
                  </FadeIn>
                </div>
              </div>

              <div className="w-full lg:w-7/12">
                <FadeIn className="space-y-12">
                  <p className="text-3xl md:text-4xl font-serif text-gray-300 leading-relaxed">
                    {t('methode.manifestoP1')}
                  </p>
                  <div className="w-16 h-px bg-gold-500/50"></div>
                  <p className="text-lg md:text-xl font-light text-gray-400 leading-relaxed">
                    {t('methode.manifestoP2')}
                  </p>
                  <p className="text-lg md:text-xl font-light text-gray-400 leading-relaxed">
                    {t('methode.manifestoP3')}
                  </p>
                </FadeIn>
              </div>

            </div>
          </div>
        </section>

        {/* SECTION 2: LE PROTOCOLE (CLICK TO OPEN MODAL) */}
        <section className="py-24 md:py-40 bg-white text-black-deep relative border-t border-gray-100">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-50 rounded-full blur-[150px] pointer-events-none"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <FadeIn className="mb-24 flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div>
                <span className="text-gold-600 tracking-[0.2em] text-xs font-bold uppercase mb-4 block">{t('methode.protocolTag')}</span>
                <h2 className="text-5xl md:text-6xl font-serif text-black-deep">{t('methode.protocolTitle')}</h2>
              </div>
              <p className="text-gray-500 font-light max-w-md text-lg">
                {t('methode.protocolDesc')}
              </p>
            </FadeIn>

            <FadeInStagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {rules.map((rule, idx) => (
                <div key={idx} className="h-full">
                  <FadeInStaggerItem className="h-full">
                    <div 
                      onClick={() => setActiveRule(idx)}
                      className="group h-full p-6 rounded-xl bg-white border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:border-gold-300 hover:shadow-[0_10px_40px_rgba(201,152,85,0.15)] hover:-translate-y-1 transition-all duration-500 cursor-pointer flex flex-col relative overflow-hidden"
                    >
                      {/* Subdued image header on the card */}
                      <div className="w-full h-32 mb-6 rounded-lg overflow-hidden relative">
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500 z-10"></div>
                        <img src={rule.image} alt={rule.title} className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" />
                        <div className="absolute top-4 left-4 z-20 text-white font-serif text-2xl drop-shadow-md">
                          0{idx + 1}
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-start mb-4 relative z-10">
                        <h3 className="font-serif text-xl text-black-deep group-hover:text-gold-600 transition-colors duration-500 relative z-10">
                          {rule.title}
                        </h3>
                        <div className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-500 group-hover:border-gold-500 transition-all duration-500 ml-4">
                          <Plus size={14} className="text-gray-400 group-hover:text-white transition-colors duration-500" />
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-500 font-light mt-auto">
                        {rule.subtitle}
                      </p>
                      
                      <div className="w-0 h-[2px] bg-gold-400 group-hover:w-full transition-all duration-700 ease-out mt-6 relative z-10"></div>
                    </div>
                  </FadeInStaggerItem>
                </div>
              ))}
            </FadeInStagger>
          </div>
          
          {/* THE LUXURY CLICK MODAL */}
          <AnimatePresence>
            {activeRule !== null && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8">
                {/* Backdrop Blur overlay */}
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setActiveRule(null)}
                  className="absolute inset-0 bg-black/80 backdrop-blur-md cursor-pointer"
                ></motion.div>
                
                {/* Modal Content */}
                <motion.div 
                  initial={{ opacity: 0, y: 30, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 20, scale: 0.97 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="relative z-10 w-full max-w-5xl h-[80vh] min-h-[500px] max-h-[700px] bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-[0_0_100px_rgba(201,152,85,0.15)] overflow-hidden flex flex-col md:flex-row"
                >
                  <button 
                    onClick={() => setActiveRule(null)}
                    className="absolute top-6 right-6 z-30 w-10 h-10 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-gold-500 hover:border-gold-500 transition-all focus:outline-none"
                  >
                    <X size={20} />
                  </button>

                  {/* Left Column: Full Image */}
                  <div className="w-full md:w-2/5 h-64 md:h-full relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black-deep/80 via-transparent to-transparent z-10"></div>
                    <img 
                      src={rules[activeRule].image} 
                      alt={rules[activeRule].title} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-10 left-10 z-20">
                      <div className="text-white/50 font-serif text-6xl drop-shadow-lg mb-2">
                        0{activeRule + 1}
                      </div>
                      <div className="text-gold-400 drop-shadow-md">
                        {rules[activeRule].icon}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Content */}
                  <div className="w-full md:w-3/5 p-10 md:p-16 flex flex-col justify-center overflow-y-auto bg-[#0a0a0a]">
                    <span className="text-gold-600 tracking-[0.2em] text-[10px] font-bold uppercase mb-4 block">{t('methode.modalTag')} 0{activeRule + 1}</span>
                    
                    <h3 className="text-3xl md:text-5xl font-serif text-white mb-4 leading-tight">
                      {rules[activeRule].title}
                    </h3>
                    
                    <h4 className="text-gold-400 font-serif italic text-xl md:text-2xl mb-10">
                      {rules[activeRule].subtitle}
                    </h4>
                    
                    <div className="w-16 h-px bg-gold-500/30 mb-10"></div>
                    
                    <p className="text-gray-300 font-light text-lg md:text-xl leading-relaxed">
                      {rules[activeRule].detail}
                    </p>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </section>

        {/* SECTION 3: LA FORMATION DE L'ÉLITE */}
        <section className="py-32 bg-[#050505] text-white border-t border-white/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,152,85,0.08)_0%,transparent_70%)] pointer-events-none"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <FadeIn>
              <span className="text-gold-400 tracking-[0.3em] text-xs font-bold uppercase mb-6 block">{t('methode.recruitmentTag')}</span>
              <h2 className="text-4xl md:text-5xl lg:text-7xl font-serif text-white mb-8 leading-tight">
                {t('methode.recruitmentTitle1')} <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 font-serif italic">{t('methode.recruitmentTitle2')}</span>
              </h2>
              <p className="text-gray-400 font-light text-lg md:text-xl leading-relaxed mb-12 max-w-2xl mx-auto">
                {t('methode.recruitmentDesc')}
              </p>
              <Link to="/recrutement" className="px-10 py-5 bg-gradient-to-r from-gold-400 to-gold-600 text-white text-sm uppercase tracking-widest rounded-full shadow-[0_10px_30px_rgba(201,152,85,0.3)] hover:shadow-[0_15px_40px_rgba(201,152,85,0.5)] hover:-translate-y-1 transition-all duration-300 inline-block font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                {t('methode.recruitmentBtn')}
              </Link>
            </FadeIn>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
